import React, { useState } from 'react';
import { SubscriptionRewardModal } from '../SubscriptionRewardModal';
import RewardProgressBar from './RewardProgressBar';
import RewardSubscriptionTotal from './RewardSubscriptionTotal';
import { RewardProgressBarContainer } from './CircularProgressBar.styled';
import { RewardProgressBarProps } from './types';
import SubscriptionRewardMobile from './SubscriptionRewardMobile';
import { application } from '../Utils/application';
import {
    calculateMonthCompletionValues,
    findTierForAmount,
    extractVoucherFromPromoCode,
    Tier,
} from './logic/calculateMonthCompletionValues';

const SubscriptionReward = (props: RewardProgressBarProps) => {
    const { title, subscriptionData, translationConfig, learnMoreConfig, storeConfig, currencyFormat } = props;
    const tiers = (storeConfig?.tiers ?? []) as Tier[];
    const minAmount = tiers[0]?.min_spend ?? 0;
    const maxMonths = tiers[0]?.consecutive_months ?? 0;
    const [show, setShow] = useState(false);
    const handleOpen = () => setShow(true);
    const handleClose = () => setShow(false);
    const rewardData = subscriptionData;

    const amountShow = parseFloat(rewardData?.approximateMonthlyTotalResponse?.currentMonthTotal ?? '0');

    // Find the appropriate tier for current month spending
    const currentTier = findTierForAmount(amountShow, tiers);
    const currentVoucher = extractVoucherFromPromoCode(currentTier?.promo_code ?? '');

    // Calculate monthCompletionValue and voucherValue
    const { monthCompletionValue, voucherValue } = calculateMonthCompletionValues({
        data: rewardData,
        minAmount,
        tiers,
        initialVoucherValue: currentVoucher,
        initialMonthCompletionValue: 0,
        maxMonths,
        today: new Date(),
    });

    const value = monthCompletionValue;
    // User doesn't qualify this month if they're below minimum spend and haven't completed all months
    const notQualifyForRewardThisMonth = amountShow < minAmount && value < maxMonths;
    return (
        <RewardProgressBarContainer>
            {application.isMobile ? (
                <SubscriptionRewardMobile
                    value={value}
                    title={title}
                    voucherValue={voucherValue}
                    maxMonths={maxMonths}
                    handleOpenSubscriptionRewardPopup={handleOpen}
                    storeConfig={storeConfig}
                    amountShow={amountShow}
                    notQualifyForRewardThisMonth={notQualifyForRewardThisMonth}
                    translationConfig={translationConfig}
                    learnMoreConfig={learnMoreConfig}
                    currencyAbbreviate={storeConfig?.currencyAbbreviate}
                    currencyFormat={currencyFormat}
                />
            ) : (
                <>
                    <RewardProgressBar
                        value={value}
                        title={title}
                        notQualifyForRewardThisMonth={notQualifyForRewardThisMonth}
                        monthCompletionValue={monthCompletionValue}
                        storeConfig={storeConfig}
                        maxMonths={maxMonths}
                        voucherValue={voucherValue}
                        handleOpenSubscriptionRewardPopup={handleOpen}
                        translationConfig={translationConfig}
                        learnMoreConfig={learnMoreConfig}
                        currencyAbbreviate={storeConfig?.currencyAbbreviate}
                        currencyFormat={currencyFormat}
                    />
                    <RewardSubscriptionTotal
                        notQualifyForRewardThisMonth={notQualifyForRewardThisMonth}
                        value={value}
                        voucherValue={voucherValue}
                        maxMonths={maxMonths}
                        title={title}
                        monthCompletionValue={monthCompletionValue}
                        storeConfig={storeConfig}
                        translationConfig={translationConfig}
                        learnMoreConfig={learnMoreConfig}
                        handleOpenSubscriptionRewardPopup={handleOpen}
                        amountShow={amountShow}
                        currencyAbbreviate={storeConfig?.currencyAbbreviate}
                        currencyFormat={currencyFormat}
                    />
                </>
            )}
            <SubscriptionRewardModal
                show={show}
                close={handleClose}
                amountShow={amountShow}
                learnMoreConfig={learnMoreConfig}
                translationConfig={translationConfig}
            />
        </RewardProgressBarContainer>
    );
};

export default SubscriptionReward;
export { SubscriptionReward };
